import { FC, Suspense, useEffect, useState } from 'react';
import SingleUnitLeftColumn from './SingleUnitLeftColumn';
import { DropdownItemModel, SkillsCurriculumLessons } from '../../pages/SingleUnit';
import { useQueryLoader } from 'react-relay';
import { getSkillProgress } from '../../graphQLOperations/SingleUnit';
import { SingleUnitGetTargetQuery$data } from '../../graphQLOperations/__generated__/SingleUnitGetTargetQuery.graphql';
import SingleUnitRightColumn from './SingleUnitRightColumn';
import { CountPerTypes, SkillsByType } from '../../models/SingleUnit';
import {
  filterOrAddSkillsByLessons,
  singleUnitHelper,
  TargetSkillNode,
} from '../../businessLogicService/getHighAndMediumRelevancesAncestries';
import { filterSkillsByStandards, withFilteredTargetSkills } from '../../businessLogicService/singleUnitFilters';
import { Class } from '../../models/Class';
import { SingleUnitSkillProgressQuery } from '../../graphQLOperations/__generated__/SingleUnitSkillProgressQuery.graphql';
import SingleUnitRightColumnWithProgress from './SingleUnitRightColumnWithProgress';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';
interface Props {
  selectedUnit: DropdownItemModel;
  selectedClass: Class[] | [];
  setClassWithoutStudent: (c: boolean) => void;
  /** Already collapsed to undefined when the filter sits in its neutral "All" state. */
  selectedLesson?: string[];
  selectedStandardIds?: string[];
  skillsCurriculumLessons?: SkillsCurriculumLessons[];
  miamiBrand: boolean;
  allSkillsByUnit: SingleUnitGetTargetQuery$data | null;
}

const SingleUnitContainer: FC<Props> = ({
  selectedUnit,
  selectedClass,
  setClassWithoutStudent,
  selectedLesson,
  selectedStandardIds,
  skillsCurriculumLessons,
  miamiBrand,
  allSkillsByUnit,
}) => {
  const [skillsTypesInUnit, setSkillsTypesInUnit] = useState<CountPerTypes>();
  const [skillsInUnit, setSkillsInUnit] = useState<SkillsByType[]>();
  const [skillsInUnitPlain, setSkillsInUnitPlain] = useState<SkillsByType[]>();
  const [targetSkillsIds, setTargetSkillsIds] = useState<string[]>([]);
  const [classesQueryLoaded, setClassesQueryLoaded] = useState<boolean>(false);
  const [keyProp, setKeyProp] = useState<number>(0);

  const [queryReference, loadQuery] = useQueryLoader<SingleUnitSkillProgressQuery>(getSkillProgress);

  useEffect(() => {
    if (selectedClass.length > 0 && selectedUnit) {
      loadQuery(
        {
          classIds: [selectedClass[0].id],
          targetIds: [selectedUnit.value],
        },
        { fetchPolicy: 'network-only' },
      );
      setClassesQueryLoaded(true);
      setKeyProp(new Date().getTime());
    }

    if (selectedClass.length === 0 && skillsInUnitPlain) {
      setClassesQueryLoaded(false);
      setSkillsInUnit(skillsInUnitPlain);
    }
    setClassWithoutStudent(false);
  }, [selectedClass, selectedUnit]);

  useEffect(() => {
    if (allSkillsByUnit && allSkillsByUnit.target) {
      // Lessons are annotated on both passes: support and extension skills keep showing their lesson
      // numbers even though the optional filters no longer narrow them.
      const annotate = (data: TargetSkillNode, lessons: string[] | undefined) =>
        miamiBrand
          ? (filterOrAddSkillsByLessons(lessons, data, skillsCurriculumLessons ?? []) as TargetSkillNode)
          : data;

      const unfiltered = singleUnitHelper(annotate(allSkillsByUnit as TargetSkillNode, undefined));
      const narrowed = singleUnitHelper(
        annotate(filterSkillsByStandards(allSkillsByUnit, selectedStandardIds) as TargetSkillNode, selectedLesson),
      );
      const dataSetForType = withFilteredTargetSkills(unfiltered.dataSetForType, narrowed.dataSetForType);

      // Counts and support/extension come from the unfiltered pass; only the target list is narrowed.
      setTargetSkillsIds(unfiltered.tsIds);
      setSkillsTypesInUnit(unfiltered.countPerTypes);
      setSkillsInUnit(dataSetForType);
      setSkillsInUnitPlain(dataSetForType);
      if (selectedClass.length && selectedUnit) {
        setKeyProp(new Date().getTime());
        setClassesQueryLoaded(true);
      }
    }
    // skillsCurriculumLessons resolves on its own query, after allSkillsByUnit. Leaving it out of the
    // dependencies stranded Miami on the first pass: the neutral selection is undefined both before
    // and after the lessons load, so nothing else here changes to pull the annotated data through.
  }, [allSkillsByUnit, selectedLesson, selectedStandardIds, skillsCurriculumLessons]);

  return (
    <div className="tw-flex tw-flex-col lg:flex-row tw-gap-y-4 lg:tw-gap-x-4">
      <div className="lg:tw-w-1/3 xl:tw-w-1/4 tw-w-full">
        {skillsTypesInUnit && <SingleUnitLeftColumn skillsTypesInUnit={skillsTypesInUnit} />}
      </div>
      <div className="lg:tw-w-2/3 xl:tw-w-3/4 tw-w-full">
        {skillsInUnit &&
          (classesQueryLoaded ? (
            <Suspense fallback={<ThreeDotLoader dark />}>
              <SingleUnitRightColumnWithProgress
                queryReference={queryReference}
                skillsInUnit={skillsInUnit}
                setSkillsInUnit={setSkillsInUnit}
                keyProp={keyProp}
                setClassWithoutStudent={setClassWithoutStudent}
                targetSkillsIds={targetSkillsIds}
                selectedStandardIds={selectedStandardIds}
                optionalFiltersApplied={!!selectedStandardIds || !!selectedLesson}
              />
            </Suspense>
          ) : (
            <Suspense fallback={<ThreeDotLoader dark />}>
              <SingleUnitRightColumn
                skillsInUnit={skillsInUnit}
                keyProp={keyProp}
                targetSkillsIds={targetSkillsIds}
                selectedStandardIds={selectedStandardIds}
                optionalFiltersApplied={!!selectedStandardIds || !!selectedLesson}
              />
            </Suspense>
          ))}
      </div>
    </div>
  );
};

export default SingleUnitContainer;
